import React, { useEffect, useState } from 'react';
import './medicalsettings.css';
import { IMedicalStandard, MedicalService } from '../../services/medical.service';

interface MedicalSettingsProps {
  isOpen: boolean;
  onClose: () => void;
}

const MedicalSettings: React.FC<MedicalSettingsProps> = ({ isOpen, onClose }) => {
  const [standards, setStandards] = useState<IMedicalStandard[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [saving, setSaving] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (isOpen) {
      fetchMedicalStandards();
    }
  }, [isOpen]);

  const fetchMedicalStandards = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await MedicalService.getMedicalStandards();
      setStandards(data);
    } catch (error: any) {
      setError(error.message || 'Не удалось загрузить медицинские нормативы');
      console.error('Error fetching medical standards:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    setError(null);
    const errorMessages: string[] = [];

    const standardLabels: Record<string, string> = {
      bodyTemperature: 'Температура тела',
      systolicPressure: 'Давление систолическое',
      diastolicPressure: 'Давление диастолическое',
      alcohol: 'Алкотест',
      heartRate: 'Пульс',
    };

    for (const standard of standards) {
      try {
        await MedicalService.updateMedicalStandard(standard);
      } catch (error: any) {
        const label = standardLabels[standard.name] || standard.name;
        errorMessages.push(`Не удалось обновить: ${label}`);
        console.error(`Ошибка сохранения норматива "${standard.name}":`, error);
      }
    }

    if (errorMessages.length > 0) {
      setError(errorMessages.join('; '));
    } else {
      onClose();
    }

    setSaving(false);
  };

  const handleInputChange = (standardName: string, field: 'minValue' | 'maxValue', value: number) => {
    setStandards((prevStandards) =>
      prevStandards.map((standard) => (standard.name === standardName ? { ...standard, [field]: value } : standard)),
    );
  };

  const getStandardByName = (name: string): IMedicalStandard | undefined => {
    return standards.find((standard) => standard.name === name);
  };

  if (!isOpen) return null;

  return (
    <div className={`modal-overlay-medical`}>
      <div className={`modal-content-medical medical-settings`}>
        <div className={`modal-header`}>
          <h2>Нормативные значения медицинских показаний</h2>
          <button className={`close-button`} onClick={onClose}>
            ×
          </button>
        </div>
        <div className={`modal-body`}>
          {loading && <div className={`loading-overlay`}>Загрузка данных...</div>}

          {error && <div className={`error-message`}>{error}</div>}

          <div className={`medical-parameter`}>
            <label>Температура тела:</label>
            <div className={`range-inputs`}>
              <span>от</span>
              <input
                type="number"
                step="0.1"
                min="30.0"
                max="45.0"
                value={getStandardByName('bodyTemperature')?.minValue ?? 34.0}
                onChange={(e) => handleInputChange('bodyTemperature', 'minValue', parseFloat(e.target.value))}
                disabled={loading || saving}
              />
              <span>до</span>
              <input
                type="number"
                step="0.1"
                min="30.0"
                max="45.0"
                value={getStandardByName('bodyTemperature')?.maxValue ?? 36.9}
                onChange={(e) => handleInputChange('bodyTemperature', 'maxValue', parseFloat(e.target.value))}
                disabled={loading || saving}
              />
            </div>
          </div>

          <div className={`medical-parameter`}>
            <label>Давление систолическое:</label>
            <div className={`range-inputs`}>
              <span>от</span>
              <input
                type="number"
                step="0.1"
                min="70.0"
                max="200.0"
                value={getStandardByName('systolicPressure')?.minValue ?? 100.0}
                onChange={(e) => handleInputChange('systolicPressure', 'minValue', parseFloat(e.target.value))}
                disabled={loading || saving}
              />
              <span>до</span>
              <input
                type="number"
                step="0.1"
                min="70.0"
                max="200.0"
                value={getStandardByName('systolicPressure')?.maxValue ?? 129.0}
                onChange={(e) => handleInputChange('systolicPressure', 'maxValue', parseFloat(e.target.value))}
                disabled={loading || saving}
              />
            </div>
          </div>

          <div className={`medical-parameter`}>
            <label>Давление диастолическое:</label>
            <div className={`range-inputs`}>
              <span>от</span>
              <input
                type="number"
                step="0.1"
                min="40.0"
                max="120.0"
                value={getStandardByName('diastolicPressure')?.minValue ?? 60.0}
                onChange={(e) => handleInputChange('diastolicPressure', 'minValue', parseFloat(e.target.value))}
                disabled={loading || saving}
              />
              <span>до</span>
              <input
                type="number"
                step="0.1"
                min="40.0"
                max="120.0"
                value={getStandardByName('diastolicPressure')?.maxValue ?? 89.0}
                onChange={(e) => handleInputChange('diastolicPressure', 'maxValue', parseFloat(e.target.value))}
                disabled={loading || saving}
              />
            </div>
          </div>

          <div className={`medical-parameter`}>
            <label>Пульс:</label>
            <div className={`range-inputs`}>
              <span>от</span>
              <input
                type="number"
                step="1"
                min="30"
                max="200"
                value={getStandardByName('heartRate')?.minValue ?? 60}
                onChange={(e) => handleInputChange('heartRate', 'minValue', parseFloat(e.target.value))}
                disabled={loading || saving}
              />
              <span>до</span>
              <input
                type="number"
                step="1"
                min="30"
                max="200"
                value={getStandardByName('heartRate')?.maxValue ?? 85}
                onChange={(e) => handleInputChange('heartRate', 'maxValue', parseFloat(e.target.value))}
                disabled={loading || saving}
              />
            </div>
          </div>

          <div className={`medical-parameter`}>
            <label>Алкотест:</label>
            <div className={`range-inputs`}>
              <span>от</span>
              <input
                type="number"
                step="0.01"
                min="0.00"
                max="1.00"
                value={getStandardByName('alcohol')?.minValue ?? 0.0}
                onChange={(e) => handleInputChange('alcohol', 'minValue', parseFloat(e.target.value))}
                disabled={loading || saving}
              />
              <span>до</span>
              <input
                type="number"
                step="0.01"
                min="0.00"
                max="1.00"
                value={getStandardByName('alcohol')?.maxValue ?? 0.35}
                onChange={(e) => handleInputChange('alcohol', 'maxValue', parseFloat(e.target.value))}
                disabled={loading || saving}
              />
            </div>
          </div>
        </div>
        <div className={`modal-footer`}>
          <button className="button cancel" onClick={onClose} disabled={saving}>
            Отменить
          </button>
          <button className="button save" onClick={handleSave} disabled={loading || saving}>
            {saving ? 'Сохранение...' : 'Сохранить'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default MedicalSettings;
